import type { AmenityType, FilterState, LaundryType, PetType } from '@/src/lib/filters/filterTypes';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import type { ListingMinMaxPriceBeds } from '@/src/types/commonTypes.type';
import { deriveModelSummaryFromListingModels } from '@/src/lib/utils/listingModelSummary';
import { policiesForPet, type PetKind } from '@/src/lib/utils/petKind';

export type BdpUnitGroupKey = 'room' | 'studio' | 'one' | 'two' | 'three' | 'fourPlus';

export type BdpUnitGroup = {
  key: BdpUnitGroupKey;
  priceDisplay: string;
  units: Array<PropertyDetailListing>;
};

const GROUP_ORDER: Array<BdpUnitGroupKey> = ['room', 'studio', 'one', 'two', 'three', 'fourPlus'];

type NumberRange = {
  min: number | null;
  max: number | null;
};

const HOUR_MS = 60 * 60 * 1000;
const CREATED_WITHIN_MS: Record<NonNullable<FilterState['createdWithin']>, number> = {
  hour: HOUR_MS,
  day: 24 * HOUR_MS,
  week: 168 * HOUR_MS,
  month: 720 * HOUR_MS,
};
const SECONDS_TIMESTAMP_MAX = 10_000_000_000;

const PET_LABELS: Record<PetType, Array<string>> = {
  cats: ['cat', 'cats'],
  dogs: ['dog', 'dogs'],
};

const LAUNDRY_LABELS: Record<LaundryType, Array<string>> = {
  inUnit: ['in unit', 'in-unit', 'washer/dryer', 'washer dryer'],
  shared: ['shared', 'on site', 'on-site', 'laundry facility', 'laundry room'],
};

const AMENITY_LABELS: Record<AmenityType, Array<string>> = {
  cooling: ['air conditioning', 'a/c', 'cooling'],
  heating: ['heating', 'heat'],
  parking: ['parking', 'garage', 'carport'],
  gatedEntry: ['gated', 'controlled access'],
  doorman: ['doorman', 'concierge'],
  fitnessCenter: ['fitness', 'gym'],
  swimmingPool: ['pool', 'swimming'],
  dishwasher: ['dishwasher'],
};

const AMENITY_FLAG_KEYS: Partial<Record<AmenityType, Array<string>>> = {
  cooling: ['airConditioning'],
  parking: ['parking'],
  fitnessCenter: ['gym'],
  swimmingPool: ['pool'],
  dishwasher: ['dishwasher'],
};

const RENTAL_TYPE_MATCHES: Record<FilterState['rentalTypes'][number], Array<string>> = {
  corporate: ['corporate'],
  rental: ['rental'],
  room: ['room', 'room_for_rent', 'roomforrent'],
  sublet: ['sublet'],
};

const PROPERTY_TYPE_MATCHES: Record<FilterState['propertyTypes'][number], Array<string>> = {
  apartment: ['apartment', 'large', 'medium', 'garden'],
  condo: ['condo'],
  duplex: ['duplex', 'divided'],
  house: ['house', 'single_family', 'singlefamily'],
  townhouse: ['townhouse'],
};

export function getBdpUnitKey(unit: PropertyDetailListing, index: number): string {
  if (unit.aliasEncoded) return unit.aliasEncoded;
  if (unit.uriMalone) return unit.uriMalone;
  if (unit.urlMaloneUnit && unit.maloneLotIdEncoded) return `${unit.maloneLotIdEncoded}-${unit.urlMaloneUnit}`;
  if (unit.unit && unit.maloneLotIdEncoded) return `${unit.maloneLotIdEncoded}-${unit.unit}`;
  if (unit.maloneLotIdEncoded) return `${unit.maloneLotIdEncoded}-${index}`;
  return `bdp-unit-${index}`;
}

function asNumber(value: unknown): number | null {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase().trim() : '';
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function getBooleanValue(record: Record<string, unknown> | null, keys: Array<string>): boolean | null {
  if (!record) return null;

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') return value;
  }

  return null;
}

function textIncludesAny(text: string, labels: Array<string>): boolean {
  return labels.some((label) => text.includes(label));
}

function getAmenitiesRecord(unit: PropertyDetailListing): Record<string, unknown> | null {
  return getRecord((unit as Record<string, unknown>).amenities);
}

function getCondensedAmenities(unit: PropertyDetailListing): Record<string, unknown> | null {
  return getRecord(getAmenitiesRecord(unit)?.condensedAmenities);
}

function getAmenityHighlights(unit: PropertyDetailListing): Array<Record<string, unknown>> {
  const highlights = getAmenitiesRecord(unit)?.highlights;
  return Array.isArray(highlights)
    ? highlights.map(getRecord).filter((value): value is Record<string, unknown> => Boolean(value))
    : [];
}

function getAmenityText(unit: PropertyDetailListing): string {
  const amenities = getAmenitiesRecord(unit);
  const rawAmenities = amenities?.amenities;
  const modelAmenities = amenities?.modelAmenities;
  const highlightText = getAmenityHighlights(unit)
    .flatMap((highlight) => [
      highlight.id,
      highlight.display,
      ...(Array.isArray(highlight.types) ? highlight.types : []),
    ])
    .filter(Boolean);
  const values = [
    ...(Array.isArray(rawAmenities) ? rawAmenities : []),
    ...(Array.isArray(modelAmenities) ? modelAmenities : []),
    ...highlightText,
  ];

  return values.map(normalizeText).filter(Boolean).join(' ');
}

function hasAmenityData(unit: PropertyDetailListing): boolean {
  const amenities = getAmenitiesRecord(unit);
  if (!amenities) return false;

  const condensed = getCondensedAmenities(unit);
  return (
    Boolean(condensed && Object.keys(condensed).length > 0) ||
    getAmenityHighlights(unit).length > 0 ||
    Array.isArray(amenities.amenities) ||
    Array.isArray(amenities.modelAmenities)
  );
}

function isConcreteBdpUnit(unit: PropertyDetailListing): boolean {
  return unit.isUnit === true || hasNonEmptyString(unit.unit) || hasNonEmptyString(unit.urlMaloneUnit);
}

function getNumberRange(unit: PropertyDetailListing, field: 'beds' | 'baths' | 'price' | 'sqft'): NumberRange {
  const minMax = unit.listingMinMaxPriceBeds;
  const modelSummary = (unit as { modelSummary?: Record<string, unknown> }).modelSummary;
  const direct = unit as Record<string, unknown>;

  if (field === 'beds') {
    const min = asNumber(minMax?.minBeds ?? modelSummary?.minBeds ?? direct.bedrooms ?? direct.beds ?? direct.numBeds);
    const max = asNumber(minMax?.maxBeds ?? modelSummary?.maxBeds ?? min);
    return { min, max };
  }

  if (field === 'baths') {
    const min = asNumber(
      minMax?.minBaths ?? modelSummary?.minBaths ?? direct.bathrooms ?? direct.baths ?? direct.numBaths,
    );
    const max = asNumber(minMax?.maxBaths ?? modelSummary?.maxBaths ?? min);
    return { min, max };
  }

  if (field === 'sqft') {
    const min = asNumber(minMax?.minSqft ?? modelSummary?.minSqft ?? direct.sqft ?? direct.sqFt ?? direct.squareFeet);
    const max = asNumber(minMax?.maxSqft ?? modelSummary?.maxSqft ?? min);
    return { min, max };
  }

  const min = asNumber(minMax?.minPrice ?? modelSummary?.minPrice ?? direct.price);
  const max = asNumber(minMax?.maxPrice ?? modelSummary?.maxPrice ?? min);
  return { min, max };
}

function modelMatchesBedroomFilter(model: Record<string, unknown>, bedrooms: FilterState['bedrooms']): boolean {
  if (bedrooms.min == null) return true;

  const beds = asNumber(model.beds ?? model.numBeds ?? model.bedrooms);
  if (beds == null) return false;
  if (bedrooms.min === bedrooms.max) return beds === bedrooms.min;
  if (bedrooms.max != null && beds > bedrooms.max) return false;
  return beds >= bedrooms.min;
}

function minMaxFromModelSummary(summary: ModelSummary): ListingMinMaxPriceBeds {
  return {
    DATA_MODEL: 'ListingMinMaxPriceBeds',
    minPrice: summary.minPrice as number,
    maxPrice: summary.maxPrice as number,
    minBeds: summary.minBeds as number,
    maxBeds: summary.maxBeds as number,
    minBaths: summary.minBaths as number,
    maxBaths: summary.maxBaths as number,
    minSqft: summary.minSqft as number,
    maxSqft: summary.maxSqft as number,
    bedDisplay: '',
    bedDisplayAllAvailable: '',
    bathDisplay: '',
    priceDisplay: '',
    priceDisplayRange: '',
    sqftDisplay: '',
    bedDisplayCompact: '',
    bedDisplaySemiCompact: '',
    bathDisplayCompact: '',
    bathDisplaySemiCompact: '',
  };
}

function unitWithFilteredBedroomSummary(unit: PropertyDetailListing, filters: FilterState): PropertyDetailListing {
  if (filters.bedrooms.min == null) return unit;

  const modelSummary = deriveModelSummaryFromListingModels(unit as Record<string, unknown>, (model) =>
    modelMatchesBedroomFilter(model, filters.bedrooms),
  );
  if (modelSummary?.minBeds == null || modelSummary.maxBeds == null) return unit;

  return {
    ...unit,
    listingMinMaxPriceBeds: minMaxFromModelSummary(modelSummary),
    modelSummary,
  };
}

function rangeIntersectsFilter(range: NumberRange, filter: NumberRange): boolean {
  if (filter.min == null && filter.max == null) return true;

  const min = range.min ?? range.max;
  const max = range.max ?? range.min;
  if (min == null || max == null) return true;

  if (filter.min != null && max < filter.min) return false;
  if (filter.max != null && min > filter.max) return false;
  return true;
}

function unitMatchesBedroomFilter(unit: PropertyDetailListing, bedrooms: FilterState['bedrooms']): boolean {
  if (bedrooms.min == null) return true;

  const { min, max } = getNumberRange(unit, 'beds');
  if (min == null && max == null) return true;

  const unitMin = min ?? max!;
  const unitMax = max ?? min!;
  if (bedrooms.min === bedrooms.max) {
    return unitMin <= bedrooms.min && unitMax >= bedrooms.min;
  }

  return rangeIntersectsFilter({ min: unitMin, max: unitMax }, { min: bedrooms.min, max: bedrooms.max });
}

function getPetPolicies(unit: PropertyDetailListing, pet: PetType): Array<Record<string, unknown>> {
  const petPolicies = getRecord(getAmenitiesRecord(unit)?.petPolicies) as Partial<
    Record<PetKind, Array<unknown>>
  > | null;
  const policies = policiesForPet(petPolicies, pet === 'cats' ? 'cat' : 'dog');
  return Array.isArray(policies)
    ? policies.map(getRecord).filter((value): value is Record<string, unknown> => Boolean(value))
    : [];
}

function getPetAllowed(unit: PropertyDetailListing, pet: PetType): boolean | null {
  // A petType can have several policies (one per petSize); allowed when any allows it.
  const allowedValues = getPetPolicies(unit, pet)
    .map((policy) => getBooleanValue(policy, ['allowed']))
    .filter((value): value is boolean => value != null);
  if (allowedValues.length > 0) return allowedValues.some(Boolean);

  const condensedPets = normalizeText(getCondensedAmenities(unit)?.pets);
  const petHighlightText = getAmenityHighlights(unit)
    .filter((highlight) => normalizeText(highlight.id) === 'pets')
    .flatMap((highlight) => [highlight.display, ...(Array.isArray(highlight.types) ? highlight.types : [])])
    .map(normalizeText)
    .join(' ');
  const text = [condensedPets, petHighlightText].filter(Boolean).join(' ');

  if (!text) return null;
  if (text.includes('none') || text.includes('no pets')) return false;
  if (textIncludesAny(text, PET_LABELS[pet])) return true;
  if (text.includes('pets allowed') || text.includes('pet friendly')) return true;
  return false;
}

function unitMatchesPetFilters(unit: PropertyDetailListing, pets: FilterState['pets']): boolean {
  if (pets.length === 0) return true;

  return pets.every((pet) => getPetAllowed(unit, pet) !== false);
}

function getLaundryText(unit: PropertyDetailListing): string {
  const condensedLaundry = normalizeText(getCondensedAmenities(unit)?.laundry);
  const laundryHighlightText = getAmenityHighlights(unit)
    .filter((highlight) => normalizeText(highlight.id) === 'laundry')
    .flatMap((highlight) => [highlight.display, ...(Array.isArray(highlight.types) ? highlight.types : [])])
    .map(normalizeText)
    .join(' ');

  return [condensedLaundry, laundryHighlightText, getAmenityText(unit)].filter(Boolean).join(' ');
}

function unitMatchesLaundryFilters(unit: PropertyDetailListing, laundryFilters: FilterState['laundry']): boolean {
  if (laundryFilters.length === 0) return true;

  const laundryText = getLaundryText(unit);
  if (!laundryText) return true;
  if (laundryText.includes('none') || laundryText.includes('no laundry')) return false;

  return laundryFilters.some((laundry) => textIncludesAny(laundryText, LAUNDRY_LABELS[laundry]));
}

function unitHasAmenity(unit: PropertyDetailListing, amenity: AmenityType): boolean {
  const condensed = getCondensedAmenities(unit);
  const flagKeys = AMENITY_FLAG_KEYS[amenity] ?? [];
  const flag = getBooleanValue(condensed, flagKeys);
  if (flag === true) return true;

  const text = getAmenityText(unit);
  return textIncludesAny(text, AMENITY_LABELS[amenity]);
}

function unitMatchesAmenityFilters(unit: PropertyDetailListing, amenities: FilterState['amenities']): boolean {
  if (amenities.length === 0) return true;
  if (!hasAmenityData(unit)) return true;

  return amenities.every((amenity) => unitHasAmenity(unit, amenity));
}

function timestampToMs(value: unknown): number | null {
  const timestamp = asNumber(value);
  if (timestamp == null) return null;
  return timestamp < SECONDS_TIMESTAMP_MAX ? timestamp * 1000 : timestamp;
}

function unitMatchesCreatedWithinFilter(
  unit: PropertyDetailListing,
  createdWithin: FilterState['createdWithin'],
): boolean {
  if (!createdWithin) return true;

  const record = unit as Record<string, unknown>;
  const activated = timestampToMs(record.activated ?? record.created);
  if (activated == null) return true;

  return activated >= Date.now() - CREATED_WITHIN_MS[createdWithin];
}

function parseDateOnly(value: unknown): number | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (lower === 'now' || lower === 'available now' || lower === 'immediate' || lower === 'today') {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now.getTime();
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const parsed = Date.parse(trimmed);
  if (!Number.isFinite(parsed)) return null;
  const date = new Date(parsed);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function getAvailabilityDate(unit: PropertyDetailListing): unknown {
  const record = unit as Record<string, unknown>;
  const details = getRecord(record.details);
  return record.availabilityDate ?? details?.availabilityDate;
}

function unitMatchesMoveInDateFilter(unit: PropertyDetailListing, moveInDate: FilterState['moveInDate']): boolean {
  const moveInBy = parseDateOnly(moveInDate.end ?? moveInDate.start);
  if (moveInBy == null) return true;

  const availability = parseDateOnly(getAvailabilityDate(unit));
  if (availability == null) return true;

  return availability <= moveInBy;
}

function unitMatchesAdditionalOptions(unit: PropertyDetailListing, options: FilterState['additionalOpts']): boolean {
  const record = unit as Record<string, unknown>;

  if (options.includes('requiresPhotos') && unit.photoCount === 0) return false;
  if (options.includes('requiresPrice')) {
    const priceRange = getNumberRange(unit, 'price');
    if (priceRange.min == null && priceRange.max == null) return false;
  }
  if (options.includes('hasOffers') && unit.hasSpecialOffers === false) return false;

  if (
    options.includes('forRentByOwner') &&
    getBooleanValue(record, ['forRentByOwner', 'listedByOwner', 'isListedByOwner']) === false
  ) {
    return false;
  }

  if (options.includes('acceptingApplications')) {
    const acceptsApplications = getBooleanValue(record, ['acceptingApplications']);
    if (acceptsApplications === false) return false;

    const applicationStatus = normalizeText(unit.rentalApplicationStatus);
    if (applicationStatus && applicationStatus !== 'accepted' && applicationStatus !== 'accepting') return false;
  }

  return true;
}

function unitMatchesPropertyTypeFilters(
  propertyType: string | undefined,
  propertyTypes: FilterState['propertyTypes'],
): boolean {
  if (propertyTypes.length === 0 || !propertyType) return true;
  const normalizedPropertyType = normalizeText(propertyType);
  return propertyTypes.some((filterType) => PROPERTY_TYPE_MATCHES[filterType].includes(normalizedPropertyType));
}

function unitMatchesRentalTypeFilters(
  listingType: string | undefined,
  rentalTypes: FilterState['rentalTypes'],
): boolean {
  if (rentalTypes.length === 0 || !listingType) return true;
  const normalizedListingType = normalizeText(listingType);
  return rentalTypes.some((filterType) => RENTAL_TYPE_MATCHES[filterType].includes(normalizedListingType));
}

function unitMatchesFilters(unit: PropertyDetailListing, filters: FilterState): boolean {
  if (!unitMatchesRentalTypeFilters(unit.listingType, filters.rentalTypes)) {
    return false;
  }

  if (!unitMatchesPropertyTypeFilters(unit.propertyType, filters.propertyTypes)) {
    return false;
  }

  if (!unitMatchesBedroomFilter(unit, filters.bedrooms)) return false;
  if (!unitMatchesPetFilters(unit, filters.pets)) return false;
  if (!unitMatchesLaundryFilters(unit, filters.laundry)) return false;
  if (!unitMatchesAmenityFilters(unit, filters.amenities)) return false;
  if (
    filters.bathrooms.min != null &&
    !rangeIntersectsFilter(getNumberRange(unit, 'baths'), {
      min: filters.bathrooms.min,
      max: null,
    })
  ) {
    return false;
  }
  if (!rangeIntersectsFilter(getNumberRange(unit, 'price'), filters.price)) return false;
  if (!rangeIntersectsFilter(getNumberRange(unit, 'sqft'), filters.sqft)) return false;
  if (filters.furnished) {
    const furnished = unit.details?.furnished ?? unit.roomForRentInfo?.roomIsFurnished;
    if (furnished === false) return false;
  }
  if (filters.restrictions === 'income' && unit.incomeRestricted === false) return false;
  if (filters.restrictions === 'senior' && unit.seniorHousing === false) return false;
  if (filters.restrictions === 'student' && unit.studentHousing === false) return false;
  if (filters.restrictions === 'military' && unit.militaryHousing === false) return false;
  if (!unitMatchesCreatedWithinFilter(unit, filters.createdWithin)) return false;
  if (!unitMatchesMoveInDateFilter(unit, filters.moveInDate)) return false;
  if (!unitMatchesAdditionalOptions(unit, filters.additionalOpts)) return false;
  if (filters.keywords.trim()) {
    const keywords = filters.keywords.toLowerCase().trim();
    const haystack = [
      unit.title,
      unit.displayName,
      unit.address?.street,
      unit.address?.city,
      unit.address?.state,
      unit.searchKeyword,
      unit.fullDescription,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (haystack && !haystack.includes(keywords)) return false;
  }

  return true;
}

function getGroupKey(unit: PropertyDetailListing): BdpUnitGroupKey {
  if (unit.listingType === 'room') return 'room';

  const beds = getNumberRange(unit, 'beds').min ?? getNumberRange(unit, 'beds').max ?? 0;
  if (beds === 0) return 'studio';
  if (beds === 1) return 'one';
  if (beds === 2) return 'two';
  if (beds === 3) return 'three';
  return 'fourPlus';
}

function getPriceSortValue(unit: PropertyDetailListing): number {
  return getNumberRange(unit, 'price').min ?? Number.MAX_SAFE_INTEGER;
}

function formatPrice(value: number): string {
  return `$${value.toLocaleString('en-US')}`;
}

function getPriceDisplay(units: Array<PropertyDetailListing>): string {
  const prices = units
    .flatMap((unit) => {
      const range = getNumberRange(unit, 'price');
      return [range.min, range.max];
    })
    .filter((value): value is number => value != null && value > 0);

  if (prices.length === 0) return '';

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min)} - ${formatPrice(max)}`;
}

export function buildBdpVisibleUnits(
  units: Array<PropertyDetailListing>,
  filters: FilterState,
  shouldApplyFilters: boolean,
): Array<PropertyDetailListing> {
  const concreteUnits = units.filter(isConcreteBdpUnit);
  return (
    shouldApplyFilters
      ? concreteUnits
          .filter((unit) => unitMatchesFilters(unit, filters))
          .map((unit) => unitWithFilteredBedroomSummary(unit, filters))
      : concreteUnits
  ).sort((a, b) => getPriceSortValue(a) - getPriceSortValue(b));
}

export function buildBdpUnitGroups(
  units: Array<PropertyDetailListing>,
  filters: FilterState,
  shouldApplyFilters: boolean,
): Array<BdpUnitGroup> {
  const visibleUnits = buildBdpVisibleUnits(units, filters, shouldApplyFilters);
  return GROUP_ORDER.flatMap((key) => {
    const groupedUnits = visibleUnits.filter((unit) => getGroupKey(unit) === key);
    if (groupedUnits.length === 0) return [];
    return [{ key, priceDisplay: getPriceDisplay(groupedUnits), units: groupedUnits }];
  });
}
